import { z } from "zod";

import { evaluateFormula } from "@acme/formula";

import { TransientRepositorySchema } from "./migration-instance";

// Base types for dimensions and metrics
export const GroupByDimensionSchema = z.enum(["language", "codeowner"]);
export type GroupByDimension = z.infer<typeof GroupByDimensionSchema>;

export const GroupBySchema = z.object({
  name: GroupByDimensionSchema,
});
export type GroupBy = z.infer<typeof GroupBySchema>;

export const AggregationTypeSchema = z.enum(["count", "sum"]);
export type AggregationType = z.infer<typeof AggregationTypeSchema>;

// Query definition
export const AggregationSchema = z.object({
  type: AggregationTypeSchema,
  dimension: z.string().optional(),
  alias: z.string(), // Variable name for formula usage
});
export type Aggregation = z.infer<typeof AggregationSchema>;

// Time range can be either fixed or dynamic
export const FixedTimeRangeSchema = z.object({
  type: z.literal("fixed"),
  start: z.string(), // ISO date string
  end: z.string(), // ISO date string
  interval: z.string(), // e.g., "1d", "1w", "1M"
});
export type FixedTimeRange = z.infer<typeof FixedTimeRangeSchema>;
export const DynamicTimeRangeSchema = z.object({
  type: z.literal("dynamic"),
  relativeTo: z.enum(["now", "custom"]),
  duration: z.object({
    value: z.number(),
    unit: z.enum(["days", "weeks"]),
  }),
  interval: z.enum(["1d", "1w"]),
});
export type DynamicTimeRange = z.infer<typeof DynamicTimeRangeSchema>;

export const TimeRangeSchema = z.discriminatedUnion("type", [
  FixedTimeRangeSchema,
  DynamicTimeRangeSchema,
]);
export type TimeRange = z.infer<typeof TimeRangeSchema>;

// Query types
export const DateSnapshotSchema = z.object({
  type: z.literal("date"),
  value: z.string().datetime(),
});

export const CommitSnapshotSchema = z.object({
  type: z.literal("commit"),
  value: z
    .string()
    .regex(/^[0-9a-f]{40}$/, "Commit SHA must be 40 characters long"),
});

export const SnapshotBaseSchema = z.discriminatedUnion("type", [
  DateSnapshotSchema,
  CommitSnapshotSchema,
]);

export type SnapshotBase = z.infer<typeof SnapshotBaseSchema>;

export const FileGlobsSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("include"),
    globs: z.array(z.string()),
  }),
  z.object({
    type: z.literal("exclude"),
    globs: z.array(z.string()),
  }),
]);

export type FileGlobs = z.infer<typeof FileGlobsSchema>;

export const AstGrepQuerySchema = z.object({
  type: z.literal("astGrep"),
  rule: z.string(),
  select: z.enum(["file_count", "match_count"]),
  repositories: z
    .array(TransientRepositorySchema)
    .nonempty("Select a repository"),
  groupBy: z.array(GroupBySchema),
  filePathFilter: FileGlobsSchema.optional().nullable(),
  snapshotBase: SnapshotBaseSchema.optional().nullable(),
});

// Query definition
export const QuerySchema = z.object({
  id: z.string(),
  name: z.string(),
  query: z.discriminatedUnion("type", [AstGrepQuerySchema]),
});
export type Query = z.infer<typeof QuerySchema>;

// Widget definitions
export const BaseWidgetSchema = z.object({
  id: z.string(),
  title: z.string().nonempty("Title is required"),
  description: z.string().optional(),
  queries: z.array(QuerySchema),
  formula: z
    .string()
    .optional()
    .superRefine((formula, ctx) => {
      if (!formula) return;
      const result = evaluateFormula(formula, Array(10).fill(0) as number[]); // Test with a dummy value
      if (!result.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.message,
        });
      }
    }),
  formulaAlias: z.string().optional(),
});
export type BaseWidget = z.infer<typeof BaseWidgetSchema>;

export const TimeseriesWidgetSchema = BaseWidgetSchema.extend({
  type: z.literal("timeseries"),
  timeRange: TimeRangeSchema.optional(),
  visualization: z.object({
    type: z.enum(["line", "bar", "area"]),
    xAxis: z.string(),
    yAxis: z.string(),
  }),
});
export type TimeseriesWidget = z.infer<typeof TimeseriesWidgetSchema>;

export const SingleNumberWidgetSchema = BaseWidgetSchema.extend({
  type: z.literal("single-number"),
  visualization: z.object({
    prefix: z.string().optional(),
    suffix: z.string().optional(),
    format: z.string().optional(),
  }),
});
export type SingleNumberWidget = z.infer<typeof SingleNumberWidgetSchema>;

export const ColumnSchema = z.object({
  field: z.string(),
  header: z.string(),
  format: z.string().optional(),
});
export type Column = z.infer<typeof ColumnSchema>;

export const TableWidgetSchema = BaseWidgetSchema.extend({
  type: z.literal("table"),
  visualization: z.object({
    columns: z.array(ColumnSchema),
    sortBy: z.string().optional(),
    sortDirection: z.enum(["asc", "desc"]).optional(),
    limit: z.number().optional(),
  }),
});
export type TableWidget = z.infer<typeof TableWidgetSchema>;

// Dashboard definition
export const WidgetPositionSchema = z.object({
  widgetId: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});
export type WidgetPosition = z.infer<typeof WidgetPositionSchema>;

export const DashboardLayoutSchema = z.object({
  grid: z.object({
    columns: z.literal(12),
    rowHeight: z.number(),
  }),
  widgetPositions: z.array(WidgetPositionSchema),
});
export type DashboardLayout = z.infer<typeof DashboardLayoutSchema>;

export const WidgetSchema = z.discriminatedUnion("type", [
  TimeseriesWidgetSchema,
  SingleNumberWidgetSchema,
  TableWidgetSchema,
]);
export type Widget = z.infer<typeof WidgetSchema>;

export const DashboardSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  widgets: z.array(WidgetSchema),
  layout: DashboardLayoutSchema,
});
export type Dashboard = z.infer<typeof DashboardSchema>;
